(ns dashboard.services.question-note
  (:require [re-frame.core :as rf]
            [dashboard.ui :as ui]
            [dashboard.subs :as common-subs]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]))

(def notes-poll-frequency
  "Polling frequency for new notes in seconds"
  15)

;; Subs

(rf/reg-sub
 ::question-notes-db
 (fn [db]
   (:question-notes db)))

(defn unread-messages-for-question [question-notes-db user questionnaire-id question-id]
  (let [email (get user :user-email)
        last-read (get-in question-notes-db [questionnaire-id :last-seen question-id])]
    (count (filter #(and (= (:type %) "message")
                         (not= (:author %) email)
                         (> (:created %) last-read))
                   (get-in question-notes-db [questionnaire-id :data question-id])))))

(rf/reg-sub
 ::question-unread-note-count
 :<- [::question-notes-db]
 :<- [::common-subs/user]
 (fn [[question-notes-db user] [_ questionnaire-id question-id]]
   (unread-messages-for-question question-notes-db user questionnaire-id question-id)))

(rf/reg-sub
 ::question-notes
 :<- [::question-notes-db]
 (fn [question-notes-db [_ questionnaire-id question-id]]
   (get-in question-notes-db [questionnaire-id :data question-id])))

(rf/reg-sub
 ::question-note-status
 :<- [::question-notes-db]
 (fn [question-notes-db]
   (get question-notes-db :question-note-status)))

(rf/reg-sub
 ::question-note-last-read-time
 :<- [::question-notes-db]
 (fn [question-notes-db [_ questionnaire-id question-id]]
   (get question-notes-db [questionnaire-id :last-seen question-id])))

(rf/reg-sub
 ::question-message-note-count
 :<- [::question-notes-db]
 (fn [question-notes-db [_ questionnaire-id question-id]]
   (count (filter #(= (:type %) "message")
                  (get-in question-notes-db [questionnaire-id :data question-id])))))

;; Events

(defn unread-notes [email last-read notes]
  (->> notes
      (filter #(and (= (:type %) "message")
                    (not= (:author %) email)
                    (> (:created %) last-read)))
      count))

(rf/reg-event-fx
 ::mark-notes-last-seen
 (fn [_ [_ questionnaire-id question-id last-seen]]
   {:http-xhrio {:method          :post
                 :uri             (str "/api/notes/question/" question-id "/last-seen")
                 :params          {:last-seen last-seen}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::mark-notes-last-seen-success questionnaire-id question-id last-seen]
                 :on-failure      [::ui/set-error "Failed to mark messages as read"]}}))

(rf/reg-event-fx
 ::mark-notes-last-seen-success
 (fn [{:keys [db]} [_ questionnaire-id question-id last-seen]]
   (let [email (get-in db [:user :user-email])
         notes (get-in db [:question-notes questionnaire-id :data question-id])]
     {:db (-> db
              (assoc-in [:question-notes questionnaire-id :last-seen question-id] last-seen)
              (assoc-in [:question-notes questionnaire-id :unread question-id] (unread-notes email last-seen notes)))
      :dispatch [::notifications/add {:message "Messages marked as read"}]})))

(rf/reg-event-fx
 ::get-notes
 (fn [{:keys [db]} [_ questionnaire-id since]]
   {:db (assoc-in db [:question-notes questionnaire-id :loading]true)
    :http-xhrio {:method          :get
                 :uri             (str "/api/notes/questionnaire/" questionnaire-id "/questions")
                 :url-params      {:since (if (some? since) (.toISOString since) "1970-01-01T00:00:00Z")}
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::get-notes-success questionnaire-id]
                 :on-failure      [::get-notes-failure]}}))

(rf/reg-event-fx
 ::get-notes-failure
 (fn [_ [_ & response]]
   (js/console.warn "Failed to get notes:" (clj->js response))))

(rf/reg-event-db
 ::get-notes-success
 (fn [db [_ questionnaire-id notes]]
   (let [email (get-in db [:user :user-email])
         notes-by-question (->> notes
                                (group-by :question-id)
                                (map (fn [[question-id notes]]
                                       [question-id (into (sorted-set-by #(compare (:created %1)
                                                                                   (:created %2)))
                                                          notes)])))]
     (-> (reduce (fn [db [question-id notes]]
                   (let [last-read (get-in db [:question-notes questionnaire-id :last-seen question-id])]
                     (-> db
                         (update-in [:question-notes questionnaire-id :data question-id]
                                    (fn [old]
                                      (into (sorted-set-by #(compare (:created %1)
                                                                     (:created %2)))
                                            (concat old notes))))
                         (assoc-in [:question-notes questionnaire-id :unread question-id] (unread-notes email last-read notes)))))
                 db
                 notes-by-question)
         (assoc-in [:question-notes questionnaire-id :loading] false)))))

(rf/reg-event-fx
 ::add-note
 (fn [{:keys [db]} [_ questionnaire-id question-id note]]
   {:db         (assoc-in db [:question-notes :question-note-status :sending] true)
    :http-xhrio {:method          :post
                 :uri             (str "/api/notes/question/" question-id)
                 :params          note
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::add-note-success questionnaire-id]
                 :on-failure      [::add-note-failure]}}))

(rf/reg-event-db
 ::add-note-success
 (fn [db [_ questionnaire-id {:keys [question-id] :as note}]]
   (-> db
       (assoc-in [:question-notes :question-note-status :sending] false)
       (update-in [:question-notes questionnaire-id :data question-id] conj note))))

(rf/reg-event-fx
 ::add-note-failure
 (fn [{:keys [db]} [_]]
   {:db (assoc-in db [:question-notes :question-note-status :sending] false)
    :dispatch [::ui/set-error "Failed to add note"]}))

(rf/reg-event-db
 ::start-polling-for-notes
 (fn [db [_ questionnaire]]
   (when-let [interval (get-in db [:page :questionnaire-detail :question-note-polling-timer])]
     (js/clearInterval interval))
   (assoc-in db [:page :questionnaire-detail :question-note-polling-timer]
             (js/setInterval #(rf/dispatch [::poll-notes questionnaire])
                             (* notes-poll-frequency 1000)))))
(rf/reg-event-db
 ::stop-polling-for-notes
 (fn [db [_]]
   (-> db
       (update-in [:page :questionnaire-detail :question-note-polling-timer] js/clearInterval)
       (update-in [:page :questionnaire-detail] dissoc :question-note-polling-timer))))

(rf/reg-event-fx
 ::poll-notes
 (fn [{:keys [db]} [_ questionnaire]]
   (let [current-roles (get-in db [:user :roles])
         last-created (-> (get-in db [:question-notes :data])
                          last
                          :created)]
     {:dispatch (if-not (contains? current-roles "authenticated")
                  [::stop-polling-for-notes]
                  [::get-notes questionnaire last-created])})))

(rf/reg-event-fx
 ::get-notes-last-seen
 (fn [_ [_ questionnaire-id]]
   {:http-xhrio {:method :get
                 :uri (str "/api/notes/questionnaire/" questionnaire-id "/last-seen")
                 :timeout         http-config/timeout
                 :format          http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success      [::set-notes-last-seen questionnaire-id]
                 :on-failure      [::ui/set-error "Failed to fetch last read times for notes"]}}))

(rf/reg-event-db
 ::set-notes-last-seen
 (fn [db [_ questionnaire-id last-read-times]]
   (assoc-in db [:question-notes questionnaire-id :last-seen] last-read-times)))
